/****************
 * WHAT THE ORB CAN WALK SOMEBODY THROUGH.
 *
 * Guillaume, 2026-09-23, on a user who asked how to find a trader worth copying
 * and said twice that he did not understand the answer:
 *
 *   "the best way to bring him to the filter button would be to make things
 *    glow. We really need to hold users by the hand and walk them through the
 *    steps by making them as easy and obvious as possible."
 *
 * The spotlight already existed — QuickLearnTour dims the page and cuts a
 * glowing hole over one element while the orb narrates. What it could not do
 * was start anywhere except step 0 of a nineteen-step tour of the whole
 * product. Telling somebody who asked one question to sit through the tour is
 * not answering them.
 *
 * So the same machinery runs these instead: short sequences, three steps at
 * most, each ending on the control the person actually needs to press.
 *
 * ── Why the ids live in a file of their own ────────────────────────────────
 *
 * The model names one of these in its answer and the backend validates the
 * name against its own copy of this list before the client ever sees it. Two
 * lists in two languages drift, so a test reads both files and fails when they
 * disagree — see tourTargets.test.ts. Changing an id means changing it in
 * three places: here, assistant_chat.go, and 00-system-prompt.md, which is
 * what teaches the model the ids exist at all.
 *
 * ── Writing a step ─────────────────────────────────────────────────────────
 *
 * `target` is a data-tour name that must exist in the app; the same test
 * guarantees it. A missing one is not fatal — the tour polls for three seconds
 * and skips — but a flow whose last step skips is a flow that answered nobody.
 *
 * Keep the body to one or two sentences. This is a card floating over a dimmed
 * screen, not an article, and the person is reading it with their hand on the
 * mouse.
 ****************/

/** sec, title, body and next are keys in locales/en/learn.json (orb.*). */
export interface TourStep {
    sec: string;
    route: string;
    target: string | null;
    title: string;
    body: string;
    next?: string;
    /*
     * Read by the phone's tour only (its speed run, mobile-app/src/lib/speedRunGuide.ts);
     * the web's guides do not use them.
     *
     * on: a page with an address of its own (a trader's page): the step lives
     * on any path this pattern matches and is not navigated to; reached from
     * anywhere else it is passed over.
     *
     * doneWhen: what the person does to go on (the web's IntentGuide `done`).
     * The spotlight then lets a tap through to the control, and the step moves
     * on by itself when the path matches, a screen's guideSignal(state) holds
     * `value` (seen on arrival too), or guideSignal(event) fires meanwhile.
     *
     * adminOnly: the backtester is admins' only (2026-10-15).
     */
    on?: string;
    doneWhen?: { path?: string; state?: string; value?: string; event?: string };
    adminOnly?: boolean;
}

export const ORB_GUIDE_EVENT = "copyfi:orbguide:start";

export const GUIDES: Record<string, TourStep[]> = {
    /* 2026-10-22, the new screens: the featured traders on Traders, then the full
       list, in Lite and Pro on web and phone. The ranked table and its filters
       are "filter-traders" (Pro's Dashboard). */
    "find-a-trader": [
        {
            sec: "orb.sec.findTrader", route: "/traders", target: "traders-featured",
            title: "orb.findATrader.1.title",
            body: "orb.findATrader.1.body",
        },
        {
            sec: "orb.sec.findTrader", route: "/traders", target: "traders-see-all", next: "orb.gotIt",
            title: "orb.findATrader.2.title",
            body: "orb.findATrader.2.body",
        },
    ],

    /* The filters live on the Dashboard's Top Traders table, which "See all
       traders" opens (?all=1), in Lite too. */
    "filter-traders": [
        {
            sec: "orb.sec.findTrader", route: "/?all=1", target: "dash-filters", next: "orb.gotIt",
            title: "orb.filterTraders.1.title",
            body: "orb.filterTraders.1.body",
        },
    ],

    /* 16 accounts stopped here: through the hard part, stuck on the easy one. */
    "create-strategy": [
        {
            sec: "orb.sec.strategy", route: "/configure", target: "sb-steps",
            title: "orb.createStrategy.1.title",
            body: "orb.createStrategy.1.body",
        },
        {
            sec: "orb.sec.strategy", route: "/configure", target: "sb-scratch", next: "orb.gotIt",
            title: "orb.createStrategy.2.title",
            body: "orb.createStrategy.2.body",
        },
    ],

    "connect-exchange": [
        {
            sec: "orb.sec.exchange", route: "/copy-trade", target: "ct-brokerkeys", next: "orb.gotIt",
            title: "orb.connectExchange.1.title",
            body: "orb.connectExchange.1.body",
        },
    ],

    "pick-strategy": [
        {
            sec: "orb.sec.strategy", route: "/copy-trade", target: "ct-model", next: "orb.gotIt",
            title: "orb.pickStrategy.1.title",
            body: "orb.pickStrategy.1.body",
        },
    ],

    "start-session": [
        {
            sec: "orb.sec.goingLive", route: "/copy-trade", target: "ct-config",
            title: "orb.startSession.1.title",
            body: "orb.startSession.1.body",
        },
        {
            sec: "orb.sec.goingLive", route: "/copy-trade", target: "ct-start", next: "orb.gotIt",
            title: "orb.startSession.2.title",
            body: "orb.startSession.2.body",
        },
    ],

    // Paper, not Backtest: Backtest is still "Coming Soon" (2026-09-27).
    "try-without-money": [
        {
            sec: "orb.sec.beforeMoney", route: "/copy-trade", target: "ct-destination", next: "orb.gotIt",
            title: "orb.tryWithoutMoney.1.title",
            body: "orb.tryWithoutMoney.1.body",
        },
    ],

    /*
     * The first step is not decoration. `ss-stop` is rendered PER RUNNING ROW,
     * so on an account with nothing running there is no such element — the step
     * polls for three seconds and skips, and a single-step guide would have
     * dimmed the screen, shown nothing and faded out. Asking how to stop a
     * session before starting one is an ordinary thing to do, so the guide
     * opens on something that is always there and still answers the question.
     */
    "stop-session": [
        {
            sec: "orb.sec.stopping", route: "/sessions", target: "nav-sessions",
            title: "orb.stopSession.1.title",
            body: "orb.stopSession.1.body",
        },
        {
            sec: "orb.sec.stopping", route: "/sessions", target: "ss-stop", next: "orb.gotIt",
            title: "orb.stopSession.2.title",
            body: "orb.stopSession.2.body",
        },
    ],

    /* 2026-10-22: the flow assistant_nudge_guide.go said was missing. A running
       session keeps the rules it started with, so the fix is three clicks:
       stop it, change the strategy, start a new session on it. */
    "adjust-rules": [
        {
            sec: "orb.sec.adjusting", route: "/sessions", target: "ss-stop",
            title: "orb.adjustRules.1.title",
            body: "orb.adjustRules.1.body",
        },
        {
            sec: "orb.sec.adjusting", route: "/configure", target: "sb-steps",
            title: "orb.adjustRules.2.title",
            body: "orb.adjustRules.2.body",
        },
        {
            sec: "orb.sec.adjusting", route: "/copy-trade", target: "ct-start", next: "orb.gotIt",
            title: "orb.adjustRules.3.title",
            body: "orb.adjustRules.3.body",
        },
    ],

    /* 2026-10-22: Learn's articles and quizzes. */
    "read-articles": [
        {
            sec: "orb.sec.learn", route: "/learn", target: "learn-articles", next: "orb.gotIt",
            title: "orb.readArticles.1.title",
            body: "orb.readArticles.1.body",
        },
    ],
    "take-a-quiz": [
        {
            sec: "orb.sec.learn", route: "/learn", target: "learn-quiz", next: "orb.gotIt",
            title: "orb.takeQuiz.1.title",
            body: "orb.takeQuiz.1.body",
        },
    ],

    /* 2026-10-22: the SL / TP button sits on an open trade inside a session, a page
       a guide cannot open for someone (it needs the session), so it starts on the
       list and says the rest. */
    "set-stop-loss": [
        {
            sec: "orb.sec.protect", route: "/sessions", target: "ss-table",
            title: "orb.setStopLoss.1.title",
            body: "orb.setStopLoss.1.body",
        },
        {
            sec: "orb.sec.protect", route: "/sessions", target: "nav-sessions", next: "orb.gotIt",
            title: "orb.setStopLoss.2.title",
            body: "orb.setStopLoss.2.body",
        },
    ],

    /* 2026-10-22: the guided first steps (trader, strategy, test, practice). */
    "first-steps": [
        {
            sec: "orb.sec.start", route: "/get-started", target: "gs-progress",
            title: "orb.firstSteps.1.title",
            body: "orb.firstSteps.1.body",
        },
        {
            sec: "orb.sec.start", route: "/get-started", target: "gs-copy", next: "orb.gotIt",
            title: "orb.firstSteps.2.title",
            body: "orb.firstSteps.2.body",
        },
    ],
};

/** Every id the model is allowed to name. Read by the drift test, not by humans. */
export const GUIDE_IDS = Object.keys(GUIDES);
